import { Button, Switch, Col, Collapse, Divider, Form, FormInstance, FormProps, Input, InputNumber, Row, Tooltip } from "antd";
import { forwardRef, useEffect, useMemo } from "react";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";

import Rateio from "@/Models/Financeiro/Titulo/Rateio";
import { Movimentacao } from "@/Models/Financeiro/Conta/Movimentacao";
import { EnumRegistroTipo } from "@/Models/Financeiro/PlanoContasTipoRegistro";
import { EnumPlanoContasTipoMovimentacao } from "@/Models/Financeiro/PlanoContasTipo";
import { EnumMovFormaPagamento } from "@/Models/Financeiro/Conta/Movimentacao/MovFormaPagamento";

import InputDate from "@/Components/Base/Input/InputDate";
import InputPreco from "@/Components/Base/Input/InputPreco";
import ContaSelect, { IContaSelectProps } from "../ContaSelect";

import CentroCustoSelect from "../../CentroConta/CentroCustoSelect";
import PlanoDeContasSelect from "../../PlanoDeContas/PlanoDeContasSelect";
import MovFormaPagamentoSelect from "./MovFormaPagamento/MovFormaPagamentoSelect";
import dayjs from "dayjs";
import { BooleanProps } from "@/utility-types";

export interface IMovimentacaoFormBasicRef extends FormInstance<MovimentacaoFormType> {

}

export interface IMovimentacaoFormBasicProps extends FormProps<MovimentacaoFormType> {
    codigo?:number;
    disable?:BooleanProps<MovimentacaoFormType>;
    camposProps ?: {
        contaSelectProps ?: IContaSelectProps
    }
}

export type MovimentacaoFormType = {
    movimentacao:Movimentacao;
}

const MovimentacaoFormBasic = forwardRef<IMovimentacaoFormBasicRef, IMovimentacaoFormBasicProps>(function MovimentacaoFormBasic(
    {
        codigo,
        form : formParam,
        disabled,
        component,
        layout,
        autoComplete,
        initialValues,
        disable,
        camposProps,
        ...props
    },
    ref
) {    
    const isAlteracao = codigo !== undefined && !isNaN(codigo) && codigo > 0;
    const [newForm] = Form.useForm<MovimentacaoFormType>();
    const form = formParam ?? newForm;

    const entrada = Form.useWatch(["movimentacao", "entrada"], form);
    const codMovFormaPagamentoForm = Form.useWatch(["movimentacao", "codMovFormaPagamento"], form);
    const conta = Form.useWatch(["movimentacao", "conta"], form);
    const rateios = Form.useWatch<Rateio[]|null>(["movimentacao", "tituloTarifa", "rateios"], form);
    const tituloTarifaValorRecebido = Form.useWatch<number | undefined>(["movimentacao", "tituloTarifa", "valorRecebido"], form);
    const codTitulo = Form.useWatch(["movimentacao", "codTitulo"], form);
    const movimentacaoFilha = Form.useWatch(["movimentacao", "movimentacaoFilha"], form);

    const codPlanoContaTarifaPadrao = useMemo(() => {
        console.log("useMemo Tarifa", entrada, conta, codMovFormaPagamentoForm);
        
        let codPlanoConta:number|undefined|null = undefined;
        if (codMovFormaPagamentoForm === EnumMovFormaPagamento.Boleto)
            codPlanoConta = entrada ? conta?.codPlanoConta_TaxasBoleto : conta?.codPlanoContasTarifaBoletoReembolso;
        else 
            codPlanoConta = entrada ? conta?.codPlanoConta_TaxasGetNet : conta?.codPlanoContasTarifaReembolso;

        return (codPlanoConta ?? 0) <= 0 ? undefined : codPlanoConta;
    }, [entrada, conta, codMovFormaPagamentoForm]);

    useEffect(() => {
        console.log("useEffect Tarifa", codPlanoContaTarifaPadrao);

        if (codPlanoContaTarifaPadrao !== undefined)
        {
            form.setFieldValue(
                ["movimentacao", "tituloTarifa", "rateios", 0, "planoConta", "codigo"],
                //valueCodPlanoContasTarifa
                codPlanoContaTarifaPadrao
            );
            
            form.setFieldValue(
                ["movimentacao", "tituloTarifa", "rateios", 0, "codPlanoConta"],
                codPlanoContaTarifaPadrao
            );
        }
    }, [codPlanoContaTarifaPadrao]);

    return (
        <Form<MovimentacaoFormType>
            {...props}
            ref={ref}
            form={form}
            disabled={disabled}
            component={component ?? "div"}
            layout={layout ?? "vertical"}
            autoComplete={autoComplete ?? "off"}
            initialValues={{
                ...initialValues,
                movimentacao: {
                    ...initialValues?.movimentacao,
                    codigo: codigo,
                    dataMovimentacao: dayjs(initialValues?.movimentacao?.dataMovimentacao).locale("pt-br"),
                    entrada: initialValues?.movimentacao?.entrada === undefined ? true : initialValues?.movimentacao?.entrada,
                    tituloTarifa: initialValues?.movimentacao?.tituloTarifa ?? {},
                    movimentacaoFilha: initialValues?.movimentacao?.movimentacaoFilha ?? {},
                },
            }}
        >
            <Row gutter={8} wrap>
                <Form.Item<MovimentacaoFormType> name={["movimentacao"]} hidden></Form.Item>

                <Col xs={6} sm={4} md={4} lg={3} xl={2} xxl={2}>
                    <Form.Item<MovimentacaoFormType> name={["movimentacao", "entrada"]} label={"Movimentação"}>
                        <Switch disabled={disabled ? undefined : disable?.movimentacao?.entrada} checkedChildren="Entrada" unCheckedChildren="Saída" />
                    </Form.Item>
                </Col>
                <Col xs={18} sm={20} md={20} lg={13} xl={6} xxl={4}>
                    <Form.Item<MovimentacaoFormType> name={["movimentacao", "codMovFormaPagamento"]} label="Forma" rules={[{ required: true }]}>
                        <MovFormaPagamentoSelect disabled={isAlteracao ? true : undefined} />
                    </Form.Item>
                </Col>
                <Form.Item<MovimentacaoFormType> name={["movimentacao", "conta"]}></Form.Item>
                <Col xs={24} sm={12} md={10} lg={8} xl={6} xxl={6}>
                    <Form.Item<MovimentacaoFormType> name={["movimentacao", "codConta"]} label={"Conta"} rules={[{ required: true }]}>
                        <ContaSelect
                            {...camposProps?.contaSelectProps}
                            options={[
                                initialValues?.movimentacao?.conta, 
                                ...(camposProps?.contaSelectProps?.options ?? [])
                            ].filter(f => f !== undefined && f !== null)}
                            filtros={{ 
                                ...camposProps?.contaSelectProps?.filtros,
                                encerrada : camposProps?.contaSelectProps?.filtros?.encerrada ?? false
                            }}
                            disabled={disabled ? undefined : disable?.movimentacao?.codConta}
                            onChange={(_, __, contaSelecao) => {
                                // form.setFieldsValue({
                                //     movimentacao: {
                                //         // conta : contaSelecao,
                                //         tituloTarifa: {
                                //             rateios: (rateios ?? []).map((m) => {
                                //                 if (m !== undefined && m !== null) {
                                //                     m.codPlanoConta = (
                                //                         (contaSelecao?.codPlanoContaTarifa ?? 0) <= 0 ? 0 : contaSelecao?.codPlanoContaTarifa
                                //                     ) as number;
                                //                     m.planoConta = {
                                //                         codigo: ((contaSelecao?.codPlanoContaTarifa ?? 0) <= 0
                                //                             ? 0
                                //                             : contaSelecao?.codPlanoContaTarifa) as number,
                                //                     };
                                //                 }
                                //                 return m;
                                //             }),
                                //         } as any,
                                //     },
                                // });

                                form.setFieldValue(["movimentacao", "conta"], contaSelecao);
                                // form.setFieldValue(["movimentacao", "tituloTarifa", "rateios", 0, "planoConta"], {
                                //     codigo: contaSelecao?.codPlanoContaTarifa,
                                // });
                            }}
                        />
                    </Form.Item>
                </Col>
                {(codMovFormaPagamentoForm === EnumMovFormaPagamento.TransferenciaBancaria || codMovFormaPagamentoForm === EnumMovFormaPagamento.Outros) && (
                    <Col xs={24} sm={12} md={10} lg={8} xl={6} xxl={6}>
                        <Form.Item
                            name={["movimentacao", "movimentacaoFilha", "codConta"]}
                            label={entrada ? "Conta origem" : "Conta destino"}
                            rules={[{ required: false }]}
                        >
                            <ContaSelect
                                filtros={{ encerrada : false, codCentrosDeCustosADV : camposProps?.contaProps?.codCentrosDeCustosADV }}
                                options={[initialValues?.movimentacaoFilha?.conta].filter(f => f !== undefined && f !== null)}
                            />
                        </Form.Item>
                    </Col>
                )}
                <Col xs={24} sm={12} md={4} lg={8} xl={4} xxl={3}>
                    <Form.Item<MovimentacaoFormType> name={["movimentacao", "dataMovimentacao"]} label="Data" rules={[{ required: true }]}>
                        <InputDate showTime disabled={disable?.movimentacao?.dataMovimentacao ?? undefined} />
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={6} lg={8} xl={4} xxl={3}>
                    <Form.Item<MovimentacaoFormType> name={["movimentacao", "valorTransacao"]} label="Valor">
                        <InputPreco placeholder="Informe" disabled={disable?.movimentacao?.valorTransacao} />
                    </Form.Item>
                </Col>
                {disable?.movimentacao?.descricao !== true && <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
                    <Form.Item<MovimentacaoFormType> name={["movimentacao", "descricao"]} label="Histórico" rules={[{ max: 128 }]}>
                        <Input.TextArea rows={1} disabled={disable?.movimentacao?.descricao ?? undefined} />
                    </Form.Item>
                </Col>}
                <Col xs={24}>
                    <Row gutter={8} wrap>
                        {disable?.movimentacao?.comprovante !== true && <Col xs={24} sm={6}>
                            <Form.Item<MovimentacaoFormType> label={"Comprovante"} name={["movimentacao", "comprovante"]} rules={[{ required: false, max: 32 }]}>
                                <Input maxLength={32} placeholder="Comprovante" disabled={disable?.movimentacao?.comprovante ?? undefined} style={{ width: "100%" }} />
                            </Form.Item>
                        </Col>}
                        {disable?.movimentacao?.urlComprovante !== true && <Col xs={24} sm={18}>
                            <Form.Item<MovimentacaoFormType> label={"URL Comprovante"} name={["movimentacao", "urlComprovante"]} rules={[{ required: false, max: 300 }]}>
                                <Input maxLength={300} placeholder="URL Comprovante" disabled={disable?.movimentacao?.urlComprovante ?? undefined} style={{ width: "100%" }} />
                            </Form.Item>
                        </Col>}
                    </Row>
                </Col>
                <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
                    <Divider style={{ marginTop: 0 }} orientation="left">
                        Informações adicionais:
                    </Divider>
                    <Collapse
                        size="small"
                        defaultActiveKey={disabled === true || isAlteracao ? ["transacao", "tarifa", "contaExterna", "cartao", "boleto"] : undefined}
                        items={[
                            {
                                key: "transacao",
                                label: "Transação",
                                children: (
                                    <Row gutter={8} wrap>
                                        <Col xs={24} sm={12} md={12} lg={8} xl={6} xxl={4}>
                                            <Form.Item<MovimentacaoFormType>
                                                name={["movimentacao", "identificadorBanco"]}
                                                label="ID. Transaçao"
                                                rules={[{ max: 50 }]}
                                            >
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={12} md={12} lg={8} xl={6} xxl={4}>
                                            <Form.Item<MovimentacaoFormType> name={["movimentacao", "nsu"]} label="Nsu" rules={[{ max: 30 }]}>
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={12} md={12} lg={8} xl={6} xxl={4}>
                                            <Form.Item<MovimentacaoFormType> name={["movimentacao", "valorMulta"]} label="Vl. Juros">
                                                <InputPreco />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={12} md={12} lg={8} xl={6} xxl={4}>
                                            <Form.Item<MovimentacaoFormType> name={["movimentacao", "valorJuros"]} label="Vl. Multa">
                                                <InputPreco />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={12} md={12} lg={8} xl={6} xxl={4}>
                                            <Form.Item<MovimentacaoFormType> name={["movimentacao", "identificadorPagador"]} label="ID. Pagador" rules={[ { max : 30 } ]}>
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                ),
                            },
                            {
                                key: "tarifa",
                                label: "Tarifa",
                                children: (
                                    <Row gutter={8} wrap>
                                        <Col xs={16} sm={18} md={8} lg={8} xl={6} xxl={4}>
                                            <Form.Item<MovimentacaoFormType> name={["movimentacao", "tituloTarifa", "valorRecebido"]} label="Valor">
                                                <InputPreco 
                                                    placeholder="Valor tarifa (R$)" 
                                                    onChange={() => {
                                                        //const valueCodPlanoContasTarifa = form.getFieldValue(["movimentacao", "conta", "codPlanoContaTarifa"]);
                                                        form.setFieldValue(
                                                            ["movimentacao", "tituloTarifa", "rateios", 0, "planoConta", "codigo"],
                                                            //valueCodPlanoContasTarifa
                                                            codPlanoContaTarifaPadrao
                                                        );
                                                    }}    
                                                />
                                                {/* <InputNumber<number>
                                                    style={{ width: "100%" }}
                                                    min={0}
                                                    precision={2}
                                                    controls={false}
                                                    placeholder="Valor"
                                                    addonBefore={<>R$</>}
                                                    onChange={() => {
                                                        const valueCodPlanoContasTarifa = form.getFieldValue(["movimentacao", "conta", "codPlanoContaTarifa"]);
                                                        form.setFieldValue(
                                                            ["movimentacao", "tituloTarifa", "rateios", 0, "planoConta", "codigo"],
                                                            valueCodPlanoContasTarifa
                                                        );
                                                    }}
                                                /> */}
                                            </Form.Item>
                                        </Col>

                                        {(tituloTarifaValorRecebido ?? 0) > 0 && <Col xs={24}>
                                                <Divider orientation="left" style={{ marginTop: 0 }}>
                                                    Rateios
                                                </Divider>
                                                <Form.List name={["movimentacao", "tituloTarifa", "rateios"]}>
                                                    {(fields, { add, remove }, { errors, warnings }) => (
                                                        <>
                                                            <Col xs={24} sm={12} md={8} lg={10} xl={10} xxl={6}>
                                                                <Form.Item name={[0, "codPlanoConta"]} hidden label="Plano de conta">
                                                                    <Input hidden />
                                                                </Form.Item>
                                                                <Form.Item name={[0, "planoConta"]} label="Plano de conta" rules={[{ required: true }]}>
                                                                    <PlanoDeContasSelect
                                                                        disabled={codPlanoContaTarifaPadrao !== undefined}
                                                                        filtros={{
                                                                            tipo: {
                                                                                codigo: entrada ? EnumPlanoContasTipoMovimentacao.Despesa : EnumPlanoContasTipoMovimentacao.Receita,
                                                                            },
                                                                            tipoRegistro: {
                                                                                codigo: EnumRegistroTipo.Item
                                                                            },
                                                                        }}
                                                                        onChange={(v) => {
                                                                            form.setFieldValue(
                                                                                ["movimentacao", "tituloTarifa", "rateios", 0, "codPlanoConta"],
                                                                                v?.codigo
                                                                            );
                                                                        }}
                                                                    />
                                                                </Form.Item>
                                                            </Col>
                                                            <Form.ErrorList warnings={warnings} errors={errors}></Form.ErrorList>
                                                        </>
                                                    )}
                                                </Form.List>
                                            </Col>
                                        }
                                    </Row>
                                ),
                            },
                            (movimentacaoFilha?.codConta ?? 0) <= 0 &&
                            (codMovFormaPagamentoForm === EnumMovFormaPagamento.TransferenciaBancaria ||
                                codMovFormaPagamentoForm === EnumMovFormaPagamento.Boleto ||
                                codMovFormaPagamentoForm === EnumMovFormaPagamento.Outros)
                                ? {
                                      key: "contaExterna",
                                      label: `Informações da conta ${entrada ? "origem" : "destino"}`,
                                      children: (
                                          <Row gutter={8} wrap>
                                              <Col xs={24} sm={12} md={12} lg={8} xl={6} xxl={4}>
                                                  <Form.Item<MovimentacaoFormType> name={["movimentacao", "bancoExterno"]} label="Banco" rules={[{ max: 3 }]}>
                                                      <Input />
                                                  </Form.Item>
                                              </Col>
                                              <Col xs={24} sm={12} md={12} lg={8} xl={6} xxl={4}>
                                                  <Form.Item<MovimentacaoFormType>
                                                      name={["movimentacao", "agenciaExterna"]}
                                                      label="Agência"
                                                      rules={[{ max: 10 }]}
                                                  >
                                                      <Input />
                                                  </Form.Item>
                                              </Col>
                                              <Col xs={24} sm={12} md={12} lg={8} xl={6} xxl={4}>
                                                  <Form.Item<MovimentacaoFormType>
                                                      name={["movimentacao", "dacAgenciaExterna"]}
                                                      label="Dig. agência"
                                                      rules={[{ max: 2 }]}
                                                  >
                                                      <Input />
                                                  </Form.Item>
                                              </Col>
                                              <Col xs={24} sm={12} md={12} lg={8} xl={6} xxl={4}>
                                                  <Form.Item<MovimentacaoFormType> name={["movimentacao", "contaExterna"]} label="Conta" rules={[{ max: 15 }]}>
                                                      <Input />
                                                  </Form.Item>
                                              </Col>
                                              <Col xs={24} sm={12} md={12} lg={8} xl={6} xxl={4}>
                                                  <Form.Item<MovimentacaoFormType>
                                                      name={["movimentacao", "dacContaExterna"]}
                                                      label="Dig. conta"
                                                      rules={[{ max: 2 }]}
                                                  >
                                                      <Input />
                                                  </Form.Item>
                                              </Col>
                                          </Row>
                                      ),
                                  }
                                : undefined,
                            codMovFormaPagamentoForm !== EnumMovFormaPagamento.Outros &&
                            codMovFormaPagamentoForm !== EnumMovFormaPagamento.Credito &&
                            codMovFormaPagamentoForm !== EnumMovFormaPagamento.Debito
                                ? undefined
                                : {
                                      key: "cartao",
                                      label: "Cartão",
                                      children: (
                                          <Row gutter={8} wrap>
                                              <Col xs={24} sm={12} md={12} lg={8} xl={6} xxl={4}>
                                                  <Form.Item<MovimentacaoFormType>
                                                      name={["movimentacao", "codigoAutorizacao"]}
                                                      label="Cód. Autorização"
                                                      rules={[{ max: 30 }]}
                                                  >
                                                      <Input />
                                                  </Form.Item>
                                              </Col>
                                              <Col xs={24} sm={12} md={12} lg={8} xl={6} xxl={4}>
                                                  <Form.Item<MovimentacaoFormType> name={["movimentacao", "bandeira"]} label="Bandeira" rules={[{ max: 30 }]}>
                                                      <Input />
                                                  </Form.Item>
                                              </Col>
                                              {(codMovFormaPagamentoForm === EnumMovFormaPagamento.Credito ||
                                                  codMovFormaPagamentoForm === EnumMovFormaPagamento.Outros) && (
                                                  <>
                                                      <Col xs={24} sm={12} md={12} lg={8} xl={6} xxl={4}>
                                                          <Form.Item<MovimentacaoFormType> name={["movimentacao", "totalParcelas"]} label="Qtd. Parcelas">
                                                              <InputNumber style={{ width: "100%" }} />
                                                          </Form.Item>
                                                      </Col>
                                                      <Col xs={24} sm={12} md={12} lg={8} xl={6} xxl={4}>
                                                          <Form.Item<MovimentacaoFormType> name={["movimentacao", "numeroParcela"]} label="Parcela">
                                                              <InputNumber style={{ width: "100%" }} />
                                                          </Form.Item>
                                                      </Col>
                                                  </>
                                              )}
                                          </Row>
                                      ),
                                  },
                            codMovFormaPagamentoForm === EnumMovFormaPagamento.Outros || codMovFormaPagamentoForm === EnumMovFormaPagamento.Boleto
                                ? {
                                      key: "boleto",
                                      label: "Boleto",
                                      children: (
                                          <Row gutter={8} wrap>
                                              <Col xs={24} sm={12} md={12} lg={8} xl={6} xxl={4}>
                                                  <Form.Item<MovimentacaoFormType>
                                                      name={["movimentacao", "nossoNumeroRetorno"]}
                                                      label="Nº no banco"
                                                      rules={[{ max: 30 }]}
                                                  >
                                                      <Input />
                                                  </Form.Item>
                                              </Col>
                                              <Col xs={24} sm={12} md={12} lg={8} xl={6} xxl={4}>
                                                  <Form.Item<MovimentacaoFormType>
                                                      name={["movimentacao", "nossoNumero"]}
                                                      label="Nosso número"
                                                      rules={[{ max: 30 }]}
                                                  >
                                                      <Input />
                                                  </Form.Item>
                                              </Col>
                                              <Col xs={24} sm={12} md={12} lg={8} xl={6} xxl={4}>
                                                  <Form.Item<MovimentacaoFormType>
                                                      name={["movimentacao", "numeroDocumento"]}
                                                      label="Nº documento"
                                                      rules={[{ max: 30 }]}
                                                  >
                                                      <Input />
                                                  </Form.Item>
                                              </Col>
                                          </Row>
                                      ),
                                  }
                                : undefined,
                        ].filter((f) => f !== undefined)}
                    />
                </Col>
            </Row>
        </Form>
    );
});

export default MovimentacaoFormBasic;